import { head } from '@/infra/rest';

export class ActivityTracker {
  private lastActivity: number = Date.now();
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private eventListeners: { [key: string]: () => void } = {};

  constructor(private readonly heartbeatTime: number = 60000) {
    this.initializeActivityListeners();
  }

  private initializeActivityListeners(): void {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      const listener = () => this.updateLastActivity();
      this.eventListeners[event] = listener;
      window.addEventListener(event, listener, { passive: true });
    });
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(async () => {
      const now = Date.now();
      const timeSinceLastActivity = now - this.lastActivity;

      // Only send heartbeat if user interacted in this tab within heartbeatTime
      if (timeSinceLastActivity > this.heartbeatTime) {
        return;
      }

      try {
        await head({
          url: '/v0/auth/session-active',
          useCookies: true,
          isAuthRequired: true,
        });

        // Update the global last activity timestamp
        localStorage.setItem('last_activity', Date.now().toString());
      } catch (error) {
        console.error('Failed to send heartbeat:', error);

        if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
          window.dispatchEvent(new CustomEvent('session-expired'));
        }
      }
    }, this.heartbeatTime);
  }

  private updateLastActivity(): void {
    this.lastActivity = Date.now();
    // Update the global activity timestamp so other tabs can see it
    localStorage.setItem('last_activity', this.lastActivity.toString());
  }

  public startTracking(): void {
    if (this.heartbeatInterval) return;
    this.startHeartbeat();
  }

  public stopTracking(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  public cleanup(): void {
    this.stopTracking();
    
    Object.entries(this.eventListeners).forEach(([event, listener]) => {
      window.removeEventListener(event, listener);
    });
    this.eventListeners = {};
  }

  public getLastActivity(): number {
    return this.lastActivity;
  }

  public isTracking(): boolean {
    return this.heartbeatInterval !== null;
  }
}
